import { Chip } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import EditIcon from '@mui/icons-material/Edit'
import SendIcon from '@mui/icons-material/Send'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ErrorIcon from '@mui/icons-material/Error'

/**
 * StatusBadge component - Pill-shaped status badge with consistent color coding
 * 
 * @param {string} status - Status value (e.g., 'active', 'completed', 'pending')
 * @param {string} size - Size: 'small' | 'medium' (default: 'small')
 * @param {boolean} showIcon - Whether to show an icon (default: false)
 * @param {string} variant - MUI variant: 'filled' | 'outlined' (default: 'filled')
 */
export const StatusBadge = ({ 
  status, 
  size = 'small', 
  showIcon = false,
  variant = 'filled',
  ...props 
}) => {
  // Normalize status to lowercase for consistency
  const normalizedStatus = status?.toLowerCase() || 'unknown'

  // Define status configurations with colors and icons
  const statusConfig = {
    // Campaign statuses
    active: {
      label: 'Active',
      color: 'success',
      icon: <CheckCircleIcon fontSize="small" />,
    },
    completed: {
      label: 'Completed',
      color: 'success',
      icon: <CheckCircleIcon fontSize="small" />,
    },
    draft: {
      label: 'Draft',
      color: 'default',
      icon: <EditIcon fontSize="small" />,
    },
    archived: {
      label: 'Archived',
      color: 'default',
      icon: null,
    },

    // Workflow stages
    outreach: {
      label: 'Outreach',
      color: 'info',
      icon: <SendIcon fontSize="small" />,
    },
    'client review': {
      label: 'Client Review',
      color: 'warning',
      icon: <VisibilityIcon fontSize="small" />,
    },
    'content creation': {
      label: 'Content Creation',
      color: 'warning',
      icon: <EditIcon fontSize="small" />,
    },
    edits: {
      label: 'Edits',
      color: 'error',
      icon: <EditIcon fontSize="small" />,
    },
    'ready to post': {
      label: 'Ready to Post',
      color: 'info',
      icon: <CheckCircleIcon fontSize="small" />,
    },

    // Influencer type badges
    'potential': {
      label: 'Potential',
      color: 'warning',
      icon: null,
    },
    approved: {
      label: 'Approved',
      color: 'success',
      icon: <CheckCircleIcon fontSize="small" />,
    },

    // Campaign roster workflow (client/admin approval loop)
    proposed: {
      label: 'Draft (not sent)',
      color: 'default',
      icon: <EditIcon fontSize="small" />,
    },
    needs_influencers: {
      label: 'Needs influencers',
      color: 'warning',
      icon: null,
    },
    revision_requested: {
      label: 'Revision requested',
      color: 'error',
      icon: null,
    },
    'pending_client_approval': {
      label: 'Pending client approval',
      color: 'warning',
      icon: <HourglassEmptyIcon fontSize="small" />,
    },
    'revision_in_progress': {
      label: 'Being revised',
      color: 'warning',
      icon: <EditIcon fontSize="small" />,
    },
    'approved_by_client': {
      label: 'Approved by client',
      color: 'success',
      icon: <CheckCircleIcon fontSize="small" />,
    },
    'rejected_by_client': {
      label: 'Rejected by client',
      color: 'error',
      icon: <ErrorIcon fontSize="small" />,
    },

    // Content/upload statuses
    pending: {
      label: 'Pending',
      color: 'warning',
      icon: <HourglassEmptyIcon fontSize="small" />,
    },
    'revision needed': {
      label: 'Revision Needed',
      color: 'error',
      icon: <EditIcon fontSize="small" />,
    },
    posted: {
      label: 'Posted',
      color: 'success',
      icon: <CheckCircleIcon fontSize="small" />,
    },

    // Payment statuses
    paid: {
      label: 'Paid',
      color: 'success',
      icon: <CheckCircleIcon fontSize="small" />,
    },
    unpaid: {
      label: 'Unpaid',
      color: 'warning',
      icon: <HourglassEmptyIcon fontSize="small" />,
    },

    // Invoice statuses
    invoice: {
      label: 'Invoice',
      color: 'info',
      icon: null,
    },

    // Error/problem status
    overdue: {
      label: 'OVERDUE',
      color: 'error',
      icon: <ErrorIcon fontSize="small" />,
    },
    'on hold': {
      label: 'On Hold',
      color: 'default',
      icon: null,
    },

    // Default fallback
    unknown: {
      label: status || 'Unknown',
      color: 'default',
      icon: null,
    },
  }

  const config = statusConfig[normalizedStatus] || statusConfig.unknown

  return (
    <Chip
      label={config.label}
      color={config.color}
      size={size}
      variant={variant}
      icon={showIcon ? config.icon : undefined}
      sx={{
        borderRadius: 2,
        fontWeight: 500,
        textTransform: normalizedStatus === 'overdue' ? 'uppercase' : 'capitalize',
        ...(variant === 'outlined' && {
          borderWidth: 1.5,
        }),
      }}
      {...props}
    />
  )
}
